import { clearAllTokens, getToken } from "@/shared/auth/tokenStorage";
import {
  createRefreshCoordinator,
  type RefreshTokenResponse,
} from "@/shared/auth/refreshCoordinator";
import { emitAuthSessionInvalid } from "@/shared/auth/authSessionEvents";
import { createApiClient } from "./apiClient";
import { ApiError } from "./errors";
import { API_BASE } from "@/shared/config/apiBase";

const baseUrl = API_BASE;

/**
 * 인증 토큰을 부착하지 않는 공개 client.
 * `/auth/login`, `/auth/refresh` 등 토큰 발급 경로 전용.
 * refresh coordinator 가 이 client 로 `/auth/refresh` 를 호출하므로
 * 인증 훅(prepareAuthToken/refreshAuthToken)을 주입하지 않아 재귀를 차단한다.
 */
export const authPublicApiClient = createApiClient({ baseUrl });

/**
 * 중앙 refresh coordinator.
 * 실제 `/auth/refresh` 호출은 공개 client 로 주입해 shared/auth 가 client 구성을 모르게 한다.
 */
export const refreshCoordinator = createRefreshCoordinator({
  requestRefresh: async (refreshToken) => {
    const { data } = await authPublicApiClient.post<RefreshTokenResponse>("/auth/refresh", {
      refreshToken,
    });
    return data;
  },
});

/**
 * access token 을 자동 부착하는 인증 client.
 * 보호 API 호출의 기본 진입점.
 * - prepareAuthToken: 호출 전 만료 임박 사전 갱신. 토큰이 없으면 네트워크 없이 로컬 거절(#405) —
 *            teardown 뒤 exit 애니메이션 중 살아 있는 옵저버의 재요청이 서버까지 가지 않게 한다.
 *            로그인 전에 이 client 를 쓰는 정상 흐름은 없다(호출 모듈 전수 감사, 2026-09-16).
 * - refreshAuthToken: 401 응답 시 refresh 후 원 요청 1회 재시도
 * - onError: 세션 유효 상태에서 USER_WITHDRAWN·USER_SUSPENDED·USER_BLOCKED 응답 시
 *            즉시 토큰 폐기 후 세션 무효 이벤트 emit (access 경로). 서버 activeViolation 은 refresh·일반 API 양쪽에서
 *            세 코드를 400 으로 반환하므로 로그인 경로(useSocialLogin)와 동일하게 세션을 무효화한다.
 * - onAuthSessionRejected: refresh 재시도까지 거친 뒤에도 401 — 세션 무효 확정 후속 처리(UNAUTHORIZED)
 */
export const apiClient = createApiClient({
  baseUrl,
  getAuthToken: () => getToken(),
  prepareAuthToken: async () => {
    // refresh 실패(SESSION_INVALID 코드 등)는 ensureValidAccessToken 이 그대로 throw 하므로 아래 null 검사에 닿지 않는다.
    const token = await refreshCoordinator.ensureValidAccessToken();
    if (!token) {
      throw new ApiError("로그인이 필요합니다.", {
        kind: "http",
        status: 401,
        errorCode: "AUTH_TOKEN_MISSING",
      });
    }
    return token;
  },
  refreshAuthToken: (hadAuthToken) => refreshCoordinator.refreshOnce(hadAuthToken),
  onError: (error) => {
    const { errorCode } = error;
    if (
      errorCode !== "USER_WITHDRAWN" &&
      errorCode !== "USER_SUSPENDED" &&
      errorCode !== "USER_BLOCKED"
    )
      return;
    clearAllTokens();
    emitAuthSessionInvalid({ errorCode, detail: error.detail });
  },
  onAuthSessionRejected: () => {
    clearAllTokens();
    emitAuthSessionInvalid({ errorCode: "UNAUTHORIZED" });
  },
});
